import React, { useEffect, useCallback, useState } from 'react';
import Header from './Header';
import { TRUSTLINE_VALIDATION_ENGINE_ADDRESSES, ContractAddress } from '../config/contractAddresses';
import { CopyIcon, CheckIcon } from './Icons';
import './ImplementationList.css';
import SiteFooter from './SiteFooter';

const ImplementationList: React.FC = () => {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  // Function to update header height CSS variable
  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector('header');
    if (header) {
      const height = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }, []);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      updateHeaderHeight();
    }, 100);
    
    const header = document.querySelector('header');
    if (header) {
      const resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      
      resizeObserver.observe(header);
      window.addEventListener('resize', updateHeaderHeight);
      
      return () => {
        clearTimeout(initialTimeout);
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateHeaderHeight);
      };
    } else {
      return () => {
        clearTimeout(initialTimeout);
      };
    }
  }, [updateHeaderHeight]);

  const copyToClipboard = async (address: string) => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(address);
        setCopiedAddress(address);
        setTimeout(() => setCopiedAddress(null), 2000);
      } else {
        // Fallback for older browsers or non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = address;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            setCopiedAddress(address);
            setTimeout(() => setCopiedAddress(null), 2000);
          } else {
            console.error('Failed to copy address: execCommand returned false');
          }
        } catch (err) {
          console.error('Failed to copy address:', err);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Failed to copy address:', err);
      // Try fallback even if clipboard API exists but fails
      try {
        const textArea = document.createElement('textarea');
        textArea.value = address;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        if (successful) {
          setCopiedAddress(address);
          setTimeout(() => setCopiedAddress(null), 2000);
        }
        document.body.removeChild(textArea);
      } catch (fallbackErr) {
        console.error('Fallback copy method also failed:', fallbackErr);
      }
    }
  };

  // Helper function to check if an address is valid
  const isValidAddress = (address: string): boolean => {
    const normalizedAddress = address.toLowerCase().trim();
    return normalizedAddress !== '0x0000000000000000000000000000000000000000' && 
           normalizedAddress !== '0' && 
           normalizedAddress !== '';
  };

  // Filter out invalid addresses first
  const validAddresses = TRUSTLINE_VALIDATION_ENGINE_ADDRESSES.filter(addr => 
    isValidAddress(addr.address)
  );

  // Flat lists for tables
  const mainnetRows = validAddresses.filter(addr => !addr.isTestnet);
  const testnetRows = validAddresses.filter(addr => addr.isTestnet);

  const renderAddressCell = (addr: ContractAddress) => (
    <div className="address-cell-with-copy">
      <code className="table-address-value">{addr.address}</code>
      <div className="table-copy-icon">
        {copiedAddress === addr.address ? (
          <CheckIcon size={18} />
        ) : (
          <CopyIcon size={18} />
        )}
      </div>
    </div>
  );

  const renderTable = (rows: ContractAddress[], title: string) => (
    <div className="chain-section">
      <h2 className="section-title">{title}</h2>
      <div className="addresses-table-wrapper">
        <table className="addresses-table">
          <thead>
            <tr>
              <th>Blockchain name</th>
              <th>Chain ID</th>
              <th>Version</th>
              <th>Address</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((addr, index) => {
              const isNewBlockchain = index === 0 || rows[index - 1].chainName !== addr.chainName;
              return (
                <tr 
                  key={`${addr.chainName}-${addr.chainId}-${index}`}
                  className={`${isNewBlockchain ? 'blockchain-separator' : 'within-blockchain'} ${copiedAddress === addr.address ? 'row-copied' : ''}`}
                  onClick={() => copyToClipboard(addr.address)}
                  title={copiedAddress === addr.address ? 'Copied!' : 'Click to copy address'}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{addr.chainName}</td>
                  <td>{addr.chainId}</td>
                  <td>{addr.version}</td>
                  <td>{renderAddressCell(addr)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="implementation-list-page">
      <Header subtitle="Supported Blockchains" showNavigation={true} />
      
      <main className="container">
        <div className="main-content">
          <div className="page-header">
            <h1>Supported Blockchains</h1>
            <p className="page-subtitle">
              List of blockchains supported by Trustline. Use the appropriate Validation Engines addresses in your smart contract configuration.
            </p>
          </div>

          <div className="tables-section">
            {mainnetRows.length > 0 && renderTable(mainnetRows, 'Mainnets')}
            {testnetRows.length > 0 && renderTable(testnetRows, 'Testnets')}
          </div>
        </div>
      </main>
      
      <SiteFooter />
    </div>
  );
};

export default ImplementationList;

